import { consts as courseConsts, opts as courseOpts } from '@views/training/training-course-intern.constants'
import {
  consts as questionConsts,
  createDefaultQuestionOptions,
  isChoiceQuestion,
  resetQuestionAnswerByType,
} from '@views/training/training-question.constants'

export const consts = {
  ...courseConsts,
  OPEN_BOOK_EXAM_OPEN: 1,
  OPEN_BOOK_EXAM_CLOSED: 2,
  SORT_TYPE_FIXED: 1,
  SORT_TYPE_RANDOM: 2,
  RECORD_STATUS_EXAMINING: 1, // 考试中
  RECORD_STATUS_PASS: 2, // 通过
  RECORD_STATUS_UNPASS: 3, // 未通过
}

export const opts = {
  isCommonMaps: courseOpts.isCommonMaps,
  InternScopeMaps: courseOpts.InternScopeMaps,
  statusMaps: courseOpts.statusMaps,
  openBookMaps: [
    {value: consts.OPEN_BOOK_EXAM_OPEN, label: '开卷'},
    {value: consts.OPEN_BOOK_EXAM_CLOSED, label: '闭卷'},
  ],
  sortTypeMaps: [
    {value: consts.SORT_TYPE_FIXED, label: '固定顺序'},
    {value: consts.SORT_TYPE_RANDOM, label: '乱序'},
  ],
  // 答卷状态
  recordStatusMaps: [
    {value: consts.RECORD_STATUS_EXAMINING, label: '考试中'},
    {value: consts.RECORD_STATUS_PASS, label: '通过'},
    {value: consts.RECORD_STATUS_UNPASS, label: '未通过'},
  ],
}

export function getOptionLabel (options, value) {
  const item = options.find(option => Number(option.value) === Number(value))
  return item ? item.label : ''
}

export function getExamStatusLabel (status) {
  return getOptionLabel(opts.statusMaps, status) || '未知状态'
}

export function getExamStatusType (status) {
  switch (Number(status)) {
    case consts.STATUS_ONLINE:
      return 'success'
    case consts.STATUS_OFFLINE:
      return 'warning'
    case consts.STATUS_BANNED:
      return 'danger'
    default:
      return 'info'
  }
}

export function getExamStatusActions (status) {
  switch (Number(status)) {
    case consts.STATUS_NEW:
      return [
        {
          name: 'online',
          label: '上架',
          type: 'success',
          apiName: 'onlineTrainingExaminationIntern',
          successText: '上架成功'
        },
      ]
    case consts.STATUS_ONLINE:
      return [
        {
          name: 'offline',
          label: '下架',
          type: 'warning',
          apiName: 'offlineTrainingExaminationIntern',
          successText: '下架成功'
        },
      ]
    case consts.STATUS_OFFLINE:
      return [
        {name: 'ban', label: '作废', type: 'danger', apiName: 'banTrainingExaminationIntern', successText: '作废成功'},
        {
          name: 'online',
          label: '上架',
          type: 'success',
          apiName: 'onlineTrainingExaminationIntern',
          successText: '上架成功'
        },
      ]
    case consts.STATUS_BANNED:
      return [
        {
          name: 'active',
          label: '激活',
          type: 'primary',
          apiName: 'activeTrainingExaminationIntern',
          successText: '激活成功'
        },
      ]
    default:
      return []
  }
}

export function isExamStructureLocked (status) {
  return Number(status) === consts.STATUS_ONLINE || Number(status) === consts.STATUS_BANNED
}

export function isExamReadonly (status) {
  return Number(status) === consts.STATUS_BANNED
}

export function isExamScopeLocked (status) {
  return Number(status) === consts.STATUS_ONLINE ||
    Number(status) === consts.STATUS_OFFLINE ||
    Number(status) === consts.STATUS_BANNED
}

export function isExamScoreLocked (status) {
  return Number(status) !== consts.STATUS_NEW
}

export function isExamAnswerLocked (status) {
  return Number(status) === consts.STATUS_ONLINE || Number(status) === consts.STATUS_BANNED
}

export function isExamOptionStructureLocked (status) {
  return Number(status) === consts.STATUS_ONLINE || Number(status) === consts.STATUS_BANNED
}

export function createExamQuestionClientId () {
  return `examq${Math.random().toString(16).slice(2)}${Date.now()}`
}

export function createExamQuestion (type = questionConsts.QUESTION_TYPE_SINGLE, overrides = {}) {
  const questionType = Number(type) || questionConsts.QUESTION_TYPE_SINGLE
  return Object.assign({
    client_id: createExamQuestionClientId(),
    source_question_id: null,
    target_usertype: null,
    customerid: null,
    training_knowledge_id: null,
    question_type: questionType,
    title: '',
    sample_files: [],
    options: isChoiceQuestion(questionType) ? createDefaultQuestionOptions() : [],
    standard_answer: resetQuestionAnswerByType(questionType),
    analysis_text: '',
    analysis_files: [],
  }, overrides)
}

function hasValue (value) {
  return value !== undefined && value !== null && value !== ''
}

// 格式化每个题目，扁平化，原因是题目组件当时重新组装了form，这里复用组件也需要重新组装一下后端或者粘贴的题目结构
export function normalizeExamQuestionFromRow (row) {
  const questionJson = row.question_json || {}
  const analysis = questionJson.question_analysis || {}
  const questionType = Number(row.question_type) || questionConsts.QUESTION_TYPE_SINGLE
  const standardAnswer = hasValue(questionJson.standard_answer) ? questionJson.standard_answer : row.standard_answer

  return createExamQuestion(questionType, {
    title: row.title || questionJson.title || '',
    source_question_id: row.id || null,
    target_usertype: hasValue(row.target_usertype) ? row.target_usertype : null,
    customerid: hasValue(row.customerid) ? row.customerid : null,
    training_knowledge_id: hasValue(row.training_knowledge_id) ? row.training_knowledge_id : null,
    sample_files: Array.isArray(questionJson.sample_files) ? questionJson.sample_files : [],
    options: Array.isArray(questionJson.options) ? questionJson.options : [],
    standard_answer: hasValue(standardAnswer) ? standardAnswer : resetQuestionAnswerByType(questionType),
    analysis_text: analysis.analysis_text || '',
    analysis_files: Array.isArray(analysis.analysis_files) ? analysis.analysis_files : [],
  })
}


// 格式化每道题目
export function getExamQuestionSubmitPayload (question) {
  const questionType = Number(question.question_type)
  const questionJson = {
    title: question.title || '',
    sample_files: question.sample_files || [],
    question_analysis: {
      analysis_text: question.analysis_text || '',
      analysis_files: question.analysis_files || [],
    },
    standard_answer: question.standard_answer,
  }

  if (isChoiceQuestion(questionType)) {
    questionJson.options = question.options || []
  }

  return {
    id: question.source_question_id || question.id || null,
    question_type: questionType,
    question_json: questionJson,
  }
}

export function getExamSubmitPayload (form) {
  return {
    title: form.title,
    target_usertype: String(form.target_usertype),
    is_common: Number(form.is_common),
    customerid: Number(form.customerid) || 0,
    training_knowledge_id: Number(form.training_knowledge_id),
    open_book_exam: Number(form.open_book_exam),
    question_score: Number(form.question_score),
    passing_score: Number(form.passing_score),
    exam_duration: Number(form.exam_duration),
    question_sort_type: Number(form.question_sort_type),
    question_options_sort_type: Number(form.question_options_sort_type),
    question_list: (form.question_list || []).map(question => getExamQuestionSubmitPayload(question)),
  }
}

export const TRAINING_EXAM_COPY_TYPE = 'training-exam-intern'

// 复制
export function buildTrainingExamCopyData (form) {
  const data = getExamSubmitPayload(form)
  return {
    version: 1,
    date: new Date().toISOString(),
    type: TRAINING_EXAM_COPY_TYPE,
    title: data.title,
    data,
  }
}

// 粘贴
export function normalizeTrainingExamCopyData (copyData) {
  if (!copyData || copyData.type !== TRAINING_EXAM_COPY_TYPE || !copyData.data) {
    return null
  }

  const data = copyData.data
  return {
    title: data.title || '',
    target_usertype: Number(data.target_usertype) || consts.TARGETUSER_TYPE_USER,
    is_common: Number(data.is_common || consts.IS_COMMON_DEDICATED),
    customerid: Number(data.customerid) > 0 ? String(data.customerid) : null,
    training_knowledge_id: data.training_knowledge_id || null,
    open_book_exam: Number(data.open_book_exam) || consts.OPEN_BOOK_EXAM_CLOSED,
    question_score: Number(data.question_score) || 10,
    passing_score: Number(data.passing_score) || 60,
    exam_duration: Number(data.exam_duration) || 60,
    question_sort_type: Number(data.question_sort_type) || consts.SORT_TYPE_FIXED,
    question_options_sort_type: Number(data.question_options_sort_type) || consts.SORT_TYPE_FIXED,
    question_list: Array.isArray(data.question_list)
      ? data.question_list.map(item => normalizeExamQuestionFromRow(item))
      : [],
  }
}


// 不做前端验证，代码先保留 重复项检查比对的是选项显示的内容，而不是内部的选项 UUID
function getChoiceAnswerIndex (answer, options) {
  return options.findIndex(option => option.uuid === answer)
}

function getChoiceDuplicateAnswer (questionType, questionJson, options) {
  if (Number(questionType) === questionConsts.QUESTION_TYPE_MULTI) {
    return (Array.isArray(questionJson.standard_answer) ? questionJson.standard_answer : [])
      .map(answer => getChoiceAnswerIndex(answer, options))
      .sort((prev, next) => prev - next)
  }
  return getChoiceAnswerIndex(questionJson.standard_answer, options)
}

export function getExamQuestionDuplicateKey (question) {
  const payload = getExamQuestionSubmitPayload(question)
  const questionJson = Object.assign({}, payload.question_json)

  if (isChoiceQuestion(payload.question_type)) {
    const options = Array.isArray(questionJson.options) ? questionJson.options : []
    // 取标题检测
    questionJson.options = options.map(option => ({
      title: option.title || '',
    }))
    questionJson.standard_answer = getChoiceDuplicateAnswer(payload.question_type, questionJson, options)
  }

  return JSON.stringify({
    question_type: payload.question_type,
    question_json: questionJson,
  })
}




// WEBPACK FOOTER //
// ./src/views/training/training-exam-intern.constants.js